// src/contexts/ShoppingCartContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import type { MenuItem } from "~/types/new_types";
import type { Branch } from "~/types/new_types";
import { useBranch } from "./BranchContext";
import { SessionStorageManager } from "~/utils/sessionStorageManager";
export type CartItem = MenuItem & {
  id: string;
  price: number;
  discounted_price?: number | null;
  quantity: number;
  specialInstructions?: string;
  restaurantId: string;
  addedAt: number;
  variants?: any[];
  isAiSuggestion?: boolean;
};

type NormalizedVariant = {
  optionName: string;
  variantName: string;
  variantPrice: number;
  isVariant: boolean;
  isMultiple: boolean;
  variantType?: string;
  actionType?: string;
  quantity: number;
};

const sanitizeVariantQuantity = (value: unknown, fallback = 1): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.max(1, Math.floor(numeric));
};

interface SanitizedVariantChild {
  name: string;
  price: number;
  imageUrl?: string;
  includedItems?: Array<{
    name: string;
    quantity: number;
    note?: string;
  }>;
}

interface SanitizedVariantEntry {
  optionName: string;
  variantName: string;
  variantPrice: number;
  variantType?: string;
  actionType?: string;
  isVariant: boolean;
  isMultiple: boolean;
  quantity: number;
  children: SanitizedVariantChild[];
}

const sanitizeVariantChild = (input: any): SanitizedVariantChild | null => {
  if (!input || typeof input !== "object") {
    return null;
  }

  const record = input as Record<string, unknown>;
  const nameCandidate =
    typeof record.name === "string" && record.name.trim()
      ? record.name.trim()
      : "";
  const price = coerceVariantPrice(record.price);
  const imageUrl =
    typeof record.imageUrl === "string" && record.imageUrl.trim()
      ? record.imageUrl.trim()
      : undefined;

  let includedItems: SanitizedVariantChild["includedItems"] | undefined;
  const comboData =
    record.comboData && typeof record.comboData === "object"
      ? (record.comboData as Record<string, unknown>)
      : null;
  const comboItems =
    comboData && Array.isArray(comboData.items) ? comboData.items : undefined;
  if (Array.isArray(comboItems)) {
    includedItems = comboItems
      .map((rawItem) => {
        if (!rawItem || typeof rawItem !== "object") {
          return null;
        }
        const itemRecord = rawItem as Record<string, unknown>;
        const itemName =
          typeof itemRecord.item_name === "string" &&
            itemRecord.item_name.trim()
            ? itemRecord.item_name.trim()
            : undefined;
        if (!itemName) {
          return null;
        }
        const itemQuantity = sanitizeVariantQuantity(itemRecord.quantity, 1);
        const itemNote =
          typeof itemRecord.note === "string" && itemRecord.note.trim()
            ? itemRecord.note.trim()
            : undefined;
        return {
          name: itemName,
          quantity: itemQuantity,
          note: itemNote,
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

    if (includedItems.length === 0) {
      includedItems = undefined;
    }
  }

  return {
    name: nameCandidate,
    price,
    imageUrl,
    includedItems,
  };
};

const sanitizeVariantEntry = (input: any): SanitizedVariantEntry | null => {
  if (!input || typeof input !== "object") {
    return null;
  }

  const record = input as Record<string, unknown>;
  const optionNameCandidate =
    (typeof record.optionName === "string" && record.optionName.trim()) ||
    (typeof record.name === "string" && record.name.trim()) ||
    (typeof record.form_name === "string" && record.form_name.trim()) ||
    "";

  const optionName = optionNameCandidate
    ? optionNameCandidate
    : typeof record.key === "string" && record.key.trim()
      ? record.key.trim()
      : "";

  if (!optionName) {
    return null;
  }

  const variantType =
    typeof record.variantType === "string"
      ? record.variantType
      : typeof record.action_type === "string" &&
        record.action_type.toLowerCase().includes("upgrade")
        ? "meals_and_combos"
        : undefined;

  const actionType =
    typeof record.actionType === "string"
      ? record.actionType
      : typeof record.action_type === "string"
        ? record.action_type
        : variantType === "meals_and_combos"
          ? "upgrade"
          : "selection";

  const isMultiple = coerceVariantBoolean(
    record.isMultiple ?? record.is_multiple ?? false,
    false
  );
  const isVariant = coerceVariantBoolean(
    record.isVariant ?? record.is_variant ?? true,
    true
  );
  const quantity = sanitizeVariantQuantity(record.quantity, 1);

  const childrenRaw = Array.isArray(record.children) ? record.children : [];
  const children = childrenRaw
    .map((child) => sanitizeVariantChild(child))
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
  const primaryChild = children[0];

  const variantName =
    (typeof record.variantName === "string" && record.variantName.trim()) ||
      (primaryChild && primaryChild.name)
      ? (primaryChild && primaryChild.name) || optionName
      : optionName;

  const variantPrice = coerceVariantPrice(
    record.variantPrice ?? primaryChild?.price ?? record.price ?? 0
  );

  return {
    optionName,
    variantName,
    variantPrice,
    variantType,
    actionType,
    isVariant,
    isMultiple,
    quantity,
    children,
  };
};

const sanitizeVariants = (variants?: any[]): SanitizedVariantEntry[] => {
  if (!Array.isArray(variants)) {
    return [];
  }

  return variants
    .map((variant) => sanitizeVariantEntry(variant))
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
};

const convertNormalizedVariantsToSanitized = (
  variants: NormalizedVariant[]
): SanitizedVariantEntry[] => {
  if (!Array.isArray(variants) || variants.length === 0) {
    return [];
  }

  return variants.map((variant) => ({
    optionName: variant.optionName,
    variantName: variant.variantName,
    variantPrice: variant.variantPrice,
    variantType: variant.variantType,
    actionType: variant.actionType,
    isVariant: variant.isVariant,
    isMultiple: variant.isMultiple,
    quantity: sanitizeVariantQuantity(variant.quantity, 1),
    children: [
      {
        name: variant.variantName,
        price: variant.variantPrice,
      },
    ],
  }));
};

const coerceVariantBoolean = (
  value: unknown,
  defaultValue = false
): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y", "on"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "n", "off"].includes(normalized)) {
      return false;
    }
  }
  return defaultValue;
};

const coerceVariantPrice = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeVariantList = (variants?: any[]): NormalizedVariant[] => {
  if (!Array.isArray(variants)) {
    return [];
  }

  const result: NormalizedVariant[] = [];

  const pushVariant = (
    optionName: string | undefined,
    variantName: string | undefined,
    variantPrice: number,
    isVariant: boolean,
    isMultiple: boolean,
    variantType?: string,
    actionType?: string,
    quantity: number = 1
  ) => {
    const normalizedOption = optionName?.toString().trim();
    const normalizedVariant = variantName?.toString().trim();
    if (!normalizedOption || !normalizedVariant) {
      return;
    }

    result.push({
      optionName: normalizedOption,
      variantName: normalizedVariant,
      variantPrice: coerceVariantPrice(variantPrice),
      isVariant,
      isMultiple,
      variantType,
      actionType,
      quantity: sanitizeVariantQuantity(quantity, 1),
    });
  };

  const visit = (
    entry: any,
    parentOptionName?: string,
    parentIsMultiple?: boolean,
    parentVariantType?: string,
    parentActionType?: string,
    parentQuantity?: number
  ) => {
    if (entry === null || entry === undefined) {
      return;
    }

    if (Array.isArray(entry)) {
      entry.forEach((child) =>
        visit(
          child,
          parentOptionName,
          parentIsMultiple,
          parentVariantType,
          parentActionType,
          parentQuantity
        )
      );
      return;
    }

    if (typeof entry === "string") {
      if (parentOptionName) {
        pushVariant(
          parentOptionName,
          entry,
          0,
          true,
          parentIsMultiple ?? false,
          parentVariantType,
          parentActionType,
          parentQuantity
        );
      }
      return;
    }

    if (typeof entry === "object") {
      const record = entry as Record<string, unknown>;

      const optionNameCandidate =
        (typeof record.optionName === "string" && record.optionName) ||
        (typeof record.option === "string" && record.option) ||
        (typeof record.name === "string" && !Array.isArray(record.children)
          ? (record.name as string)
          : parentOptionName);

      const variantNameCandidate =
        (typeof record.variantName === "string" && record.variantName) ||
        (typeof record.value === "string" && record.value) ||
        (typeof record.selection === "string" && record.selection) ||
        (typeof record.label === "string" && record.label) ||
        (typeof record.name === "string" && !Array.isArray(record.children)
          ? (record.name as string)
          : undefined);

      if (optionNameCandidate && variantNameCandidate) {
        pushVariant(
          optionNameCandidate,
          variantNameCandidate,
          coerceVariantPrice(
            record.variantPrice ??
            record.price ??
            record.addonPrice ??
            record.addon_price ??
            0
          ),
          coerceVariantBoolean(
            record.isVariant ?? record.is_variant ?? record.variant,
            true
          ),
          coerceVariantBoolean(
            record.isMultiple ??
            record.is_multiple ??
            record.multiple ??
            record.isMultipleSelection ??
            parentIsMultiple ??
            false,
            false
          ),
          typeof record.variantType === "string"
            ? record.variantType
            : parentVariantType,
          typeof record.actionType === "string"
            ? record.actionType
            : typeof record.action_type === "string"
              ? record.action_type
              : parentActionType,
          sanitizeVariantQuantity(record.quantity, parentQuantity ?? 1)
        );
      }

      if (Array.isArray(record.children) && record.children.length > 0) {
        const optionLabel =
          typeof record.name === "string"
            ? (record.name as string)
            : optionNameCandidate;
        const isMultiple = coerceVariantBoolean(
          record.isMultiple ?? record.is_multiple ?? record.multiple,
          record.children.length > 1
        );

        record.children.forEach((child) =>
          visit(
            child,
            optionLabel ?? parentOptionName,
            isMultiple,
            typeof record.variantType === "string"
              ? record.variantType
              : parentVariantType,
            typeof record.actionType === "string"
              ? record.actionType
              : typeof record.action_type === "string"
                ? record.action_type
                : parentActionType,
            sanitizeVariantQuantity(record.quantity, parentQuantity ?? 1)
          )
        );
      }

      Object.keys(record).forEach((key) => {
        if (
          key === "optionName" ||
          key === "variantName" ||
          key === "children" ||
          key === "comboData" ||
          key === "items" ||
          key === "includedItems" ||
          key === "note" ||
          key === "item_name" ||
          key === "itemId" ||
          key === "item_id"
        ) {
          return;
        }
        visit(
          record[key],
          parentOptionName,
          parentIsMultiple,
          typeof record.variantType === "string"
            ? record.variantType
            : parentVariantType,
          typeof record.actionType === "string"
            ? record.actionType
            : typeof record.action_type === "string"
              ? record.action_type
              : parentActionType,
          sanitizeVariantQuantity(record.quantity, parentQuantity ?? 1)
        );
      });
    }
  };

  variants.forEach((entry) =>
    visit(entry, undefined, undefined, undefined, undefined, 1)
  );

  return result
    .filter((variant) => variant.optionName && variant.variantName)
    .sort((a, b) => {
      const optionCompare = a.optionName.localeCompare(b.optionName);
      if (optionCompare !== 0) {
        return optionCompare;
      }
      return a.variantName.localeCompare(b.variantName);
    });
};

const buildVariantKeyFromNormalized = (
  variants: NormalizedVariant[]
): string => {
  if (!variants.length) {
    return "";
  }

  return JSON.stringify(
    variants.map((variant) => ({
      optionName: variant.optionName.toLowerCase(),
      variantName: variant.variantName.toLowerCase(),
      isVariant: variant.isVariant ? 1 : 0,
      isMultiple: variant.isMultiple ? 1 : 0,
      variantPrice: Number.isFinite(variant.variantPrice)
        ? Number(variant.variantPrice).toFixed(4)
        : "0.0000",
      variantType: variant.variantType ? variant.variantType.toLowerCase() : "",
      actionType: variant.actionType ? variant.actionType.toLowerCase() : "",
      quantity: sanitizeVariantQuantity(variant.quantity, 1),
    }))
  );
};

const getVariantComparisonData = (variants?: any[]) => {
  const normalizedList = normalizeVariantList(variants);
  return {
    normalizedList,
    key: buildVariantKeyFromNormalized(normalizedList),
  };
};

const variantsMatchByName = (a: NormalizedVariant, b: NormalizedVariant) =>
  a.optionName.toLowerCase() === b.optionName.toLowerCase() &&
  a.variantName.toLowerCase() === b.variantName.toLowerCase();

const isVariantSubset = (
  candidate: NormalizedVariant[],
  target: NormalizedVariant[]
): boolean => {
  if (!candidate.length || candidate.length > target.length) {
    return false;
  }

  return candidate.every((candidateVariant) =>
    target.some((targetVariant) =>
      variantsMatchByName(candidateVariant, targetVariant)
    )
  );
};

const removeVariantsFromList = (
  source: NormalizedVariant[],
  toRemove: NormalizedVariant[]
): NormalizedVariant[] => {
  if (!source.length || !toRemove.length) {
    return source;
  }

  return source.filter(
    (variant) =>
      !toRemove.some((targetVariant) =>
        variantsMatchByName(targetVariant, variant)
      )
  );
};

export interface TipOption {
  percentage: number;
  amount: number;
}

export interface CartSummary {
  subtotal: number;
  tax: number;
  tip: number;
  total: number;
  itemCount: number;
}

export interface ShippingInfo {
  name: string;
  tableNumber: string;
  orderNote: string;
}

interface ShoppingCartContextType {
  // Cart state
  cartItems: CartItem[];
  isCartOpen: boolean;
  currentRestaurantId: string | null;
  isCartDisabled: boolean;

  // Cart calculations
  cartSummary: CartSummary;
  selectedTipPercentage: number;

  // Customer info
  shippingInfo: ShippingInfo;

  // Temporary quantities (before adding to cart) - simplified structure
  tempQuantities: Record<string, { quantity: number; variants: any[]; isAiSuggestion?: boolean }>;

  // Actions
  addToCart: (
    item: MenuItem,
    restaurantId: string,
    quantity?: number,
    specialInstructions?: string,
    isAiSuggestion?: boolean
  ) => void;
  addToCartWithVariants: (
    item: MenuItem,
    restaurantId: string,
    quantity?: number,
    variants?: any[],
    specialInstructions?: string,
    isAiSuggestion?: boolean
  ) => void;
  removeFromCart: (itemId: string) => void;
  removeFromCartWithVariants: (
    itemId: string,
    variants?: any[],
    quantity?: number
  ) => void;
  updateCartItemQuantity: (itemId: string, quantity: number) => void;
  updateSpecialInstructions: (itemId: string, instructions: string) => void;
  clearCart: () => void;

  // Temporary quantity actions
  setTempQuantity: (
    itemId: string,
    quantity: number,
    variants?: any[],
    isAiSuggestion?: boolean
  ) => void;
  getTempQuantity: (itemId: string) => number;
  commitTempQuantities: () => void;
  clearTempQuantities: () => void;

  // UI actions
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;

  // Checkout actions
  setTipPercentage: (percentage: number) => void;
  updateShippingInfo: (info: Partial<ShippingInfo>) => void;
  checkout: () => Promise<void>;

  // Utility
  getItemQuantity: (itemId: string) => number;
  getVariantSubsetQuantity: (itemId: string, variants?: any[]) => number;
  getVariantQuantity: (itemId: string, variants?: any[]) => number;
  isItemInCart: (itemId: string) => boolean;
}

const ShoppingCartContext = createContext<ShoppingCartContextType | undefined>(
  undefined
);

interface ShoppingCartProviderProps {
  children: ReactNode;
  taxRate?: number;
}

export const ShoppingCartProvider: React.FC<ShoppingCartProviderProps> = ({
  children,
  taxRate = 0.08, // 8% default tax rate
}) => {
  const { theme } = useBranch(); // Get branch theme to check if cart is disabled
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [currentRestaurantId, setCurrentRestaurantId] = useState<string | null>(
    null
  );
  const [selectedTipPercentage, setSelectedTipPercentage] = useState(15);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    name: "",
    tableNumber: "",
    orderNote: "",
  });
  const [tempQuantities, setTempQuantities] = useState<
    Record<string, { quantity: number; variants: any[]; isAiSuggestion?: boolean }>
  >({});

  // Check if cart functionality is disabled based on branch theme
  // NOTE: theme.isAddToCart = true means cart IS enabled, so we invert it
  const isCartDisabled = !(theme?.isAddToCart ?? true);
  // Calculate cart summary
  const cartSummary: CartSummary = React.useMemo(() => {
    const subtotal = cartItems.reduce((sum, item) => {
      const price = item.discounted_price || item.price;
      return sum + price * item.quantity;
    }, 0);

    const tax = subtotal * taxRate;
    const tip = subtotal * (selectedTipPercentage / 100);
    const total = subtotal + tax + tip;
    const itemCount = cartItems.reduce(
      (count, item) => count + item.quantity,
      0
    );

    return { subtotal, tax, tip, total, itemCount };
  }, [cartItems, selectedTipPercentage, taxRate]);

  // Cart actions
  const addToCart = (
    item: MenuItem,
    restaurantId: string,
    quantity: number = 1,
    specialInstructions?: string,
    isAiSuggestion?: boolean
  ) => {
    // Early return if cart is disabled
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch. theme.isAddToCart:", theme?.isAddToCart);
      return;
    }

    if (
      currentRestaurantId &&
      currentRestaurantId !== restaurantId &&
      cartItems.length > 0
    ) {
      // Clear cart when switching restaurants
      if (
        window.confirm(
          "You have items from another restaurant in your cart. Would you like to clear your cart and add this item?"
        )
      ) {
        setCartItems([]);
        setCurrentRestaurantId(restaurantId);
      } else {
        return;
      }
    }

    setCurrentRestaurantId(restaurantId);

    const existingItemIndex = cartItems.findIndex(
      (cartItem) => cartItem.id === item.id
    );

    if (existingItemIndex > -1) {
      // Item already in cart, update quantity
      const newCartItems = [...cartItems];
      newCartItems[existingItemIndex].quantity += quantity;
      if (specialInstructions) {
        newCartItems[existingItemIndex].specialInstructions =
          specialInstructions;
      }
      setCartItems(newCartItems);
    } else {
      // New item, add to cart
      const cartItem: CartItem = {
        ...item,
        quantity,
        specialInstructions,
        restaurantId,
        addedAt: Date.now(),
        isAiSuggestion: isAiSuggestion ?? false,
      };
      setCartItems([...cartItems, cartItem]);
    }
  };

  const addToCartWithVariants = (
    item: MenuItem,
    restaurantId: string,
    quantity: number = 1,
    variants: any[] = [],
    specialInstructions?: string,
    isAiSuggestion?: boolean
  ) => {
    // Early return if cart is disabled
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch");
      return;
    }

    const normalizedQuantity = Number.isFinite(quantity)
      ? Math.max(1, Math.floor(quantity))
      : 1;

    // Check if adding items from a different restaurant
    if (
      currentRestaurantId &&
      currentRestaurantId !== restaurantId &&
      cartItems.length > 0
    ) {
      // Clear cart when switching restaurants
      if (
        window.confirm(
          "You have items from another restaurant in your cart. Would you like to clear your cart and add this item?"
        )
      ) {
        setCartItems([]);
        setCurrentRestaurantId(restaurantId);
      } else {
        return;
      }
    }

    setCurrentRestaurantId(restaurantId);

    const sanitizedVariants = sanitizeVariants(variants);
    const targetVariantData = getVariantComparisonData(sanitizedVariants);
    const normalizedTargetList = targetVariantData.normalizedList;

    setCartItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex((cartItem) => {
        if (cartItem.id !== item.id) {
          return false;
        }
        const { key } = getVariantComparisonData(cartItem.variants);
        return key === targetVariantData.key;
      });

      if (existingItemIndex > -1) {
        const updatedItems = [...prevItems];
        const existingItem = updatedItems[existingItemIndex];

        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + normalizedQuantity,
          specialInstructions:
            specialInstructions ?? existingItem.specialInstructions,
          variants:
            sanitizedVariants.length > 0
              ? sanitizedVariants
              : existingItem.variants,
        };
        return updatedItems;
      }

      const subsetIndex = prevItems.findIndex((cartItem) => {
        if (cartItem.id !== item.id) {
          return false;
        }
        const { normalizedList } = getVariantComparisonData(cartItem.variants);
        if (!normalizedList.length || !normalizedTargetList.length) {
          return false;
        }
        return isVariantSubset(normalizedList, normalizedTargetList);
      });

      if (subsetIndex > -1) {
        const updatedItems = [...prevItems];
        const existingItem = updatedItems[subsetIndex];

        updatedItems[subsetIndex] = {
          ...existingItem,
          quantity: normalizedQuantity,
          specialInstructions:
            specialInstructions ?? existingItem.specialInstructions,
          variants:
            sanitizedVariants.length > 0
              ? sanitizedVariants
              : existingItem.variants,
        };
        return updatedItems;
      }

      const fallbackIndex = prevItems.findIndex((cartItem) => {
        if (cartItem.id !== item.id) {
          return false;
        }
        const { key } = getVariantComparisonData(cartItem.variants);
        return !key;
      });

      if (fallbackIndex > -1) {
        const updatedItems = [...prevItems];
        const fallbackItem = updatedItems[fallbackIndex];

        // Replace the base entry with the new variant selection
        updatedItems[fallbackIndex] = {
          ...fallbackItem,
          quantity: normalizedQuantity,
          specialInstructions:
            specialInstructions ?? fallbackItem.specialInstructions,
          variants:
            sanitizedVariants.length > 0
              ? sanitizedVariants
              : fallbackItem.variants,
        };

        return updatedItems;
      }

      // No exact match or base item exists; create a new variant entry
      const cartItem: CartItem = {
        ...item,
        quantity: normalizedQuantity,
        specialInstructions,
        restaurantId,
        addedAt: Date.now(),
        variants: sanitizedVariants.length > 0 ? sanitizedVariants : undefined,
      };
      return [...prevItems, cartItem];
    });
  };

  const removeFromCart = (itemId: string) => {
    // Early return if cart is disabled
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch");
      return;
    }

    const newCartItems = cartItems.filter((item) => item.id !== itemId);
    setCartItems(newCartItems);

    // Clear restaurant ID if cart becomes empty
    if (newCartItems.length === 0) {
      setCurrentRestaurantId(null);
    }
  };

  const removeFromCartWithVariants = (
    itemId: string,
    variants: any[] = [],
    quantity: number = 1
  ) => {
    // Early return if cart is disabled
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch");
      return;
    }

    const normalizedQuantity = Number.isFinite(quantity)
      ? Math.max(0, Math.floor(quantity))
      : 0;
    const targetData = getVariantComparisonData(variants);
    const isVariantOnlyRemoval =
      normalizedQuantity === 0 && targetData.normalizedList.length > 0;

    setCartItems((prevItems) => {
      let handled = false;

      const nextItems = prevItems.reduce<CartItem[]>((acc, item) => {
        if (item.id !== itemId) {
          acc.push(item);
          return acc;
        }

        const existingData = getVariantComparisonData(item.variants);
        const isExactMatch =
          Boolean(targetData.key) && existingData.key === targetData.key;
        const isSubsetMatch =
          targetData.normalizedList.length > 0 &&
          isVariantSubset(
            targetData.normalizedList,
            existingData.normalizedList
          );
        const shouldModify =
          (!targetData.key && !handled) || isExactMatch || isSubsetMatch;

        if (!shouldModify) {
          acc.push(item);
          return acc;
        }

        handled = true;

        // Handle exact variant match with variant-only removal (quantity = 0)
        // This means remove these exact variants, converting to base item
        if (isVariantOnlyRemoval && isExactMatch) {
          // Convert to base item (no variants)
          acc.push({
            ...item,
            variants: undefined,
          });
          return acc;
        }

        if (isVariantOnlyRemoval && isSubsetMatch && !isExactMatch) {
          const remainingVariants = removeVariantsFromList(
            existingData.normalizedList,
            targetData.normalizedList
          );

          // If no variants remain, keep the base item without variants
          // If some variants remain, convert back to original format
          const originalFormatVariants =
            remainingVariants.length > 0
              ? remainingVariants.map((variant) => ({
                optionName: variant.optionName,
                variantName: variant.variantName,
                variantPrice: variant.variantPrice,
                isVariant: variant.isVariant,
                isMultiple: variant.isMultiple,
              }))
              : undefined;

          acc.push({
            ...item,
            variants: originalFormatVariants,
          });
          return acc;
        }

        if (isSubsetMatch && !isExactMatch) {
          const remainingVariants = removeVariantsFromList(
            existingData.normalizedList,
            targetData.normalizedList
          );

          // If no variants remain, keep the base item without variants
          // If some variants remain, convert back to original format
          const originalFormatVariants =
            remainingVariants.length > 0
              ? remainingVariants.map((variant) => ({
                optionName: variant.optionName,
                variantName: variant.variantName,
                variantPrice: variant.variantPrice,
                isVariant: variant.isVariant,
                isMultiple: variant.isMultiple,
              }))
              : undefined;

          acc.push({
            ...item,
            variants: originalFormatVariants,
          });
          return acc;
        }

        const removalQuantity = normalizedQuantity > 0 ? normalizedQuantity : 1;
        const remainingQuantity = item.quantity - removalQuantity;

        if (remainingQuantity > 0) {
          // Convert back to original format
          const originalFormatVariants =
            existingData.normalizedList.length > 0
              ? existingData.normalizedList.map((variant) => ({
                optionName: variant.optionName,
                variantName: variant.variantName,
                variantPrice: variant.variantPrice,
                isVariant: variant.isVariant,
                isMultiple: variant.isMultiple,
              }))
              : undefined;

          acc.push({
            ...item,
            quantity: remainingQuantity,
            variants: originalFormatVariants,
          });
        }

        return acc;
      }, []);

      if (!handled && !targetData.key) {
        const filtered = prevItems.filter((item) => item.id !== itemId);
        if (filtered.length === 0) {
          setCurrentRestaurantId(null);
        }
        return filtered;
      }

      if (!handled) {
        return prevItems;
      }

      if (nextItems.length === 0) {
        setCurrentRestaurantId(null);
      }

      return nextItems;
    });
  };

  const updateCartItemQuantity = (itemId: string, quantity: number) => {
    // Early return if cart is disabled
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch");
      return;
    }

    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    const newCartItems = cartItems.map((item) =>
      item.id === itemId ? { ...item, quantity } : item
    );
    setCartItems(newCartItems);
  };

  const updateSpecialInstructions = (itemId: string, instructions: string) => {
    // Early return if cart is disabled
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch");
      return;
    }

    const newCartItems = cartItems.map((item) =>
      item.id === itemId ? { ...item, specialInstructions: instructions } : item
    );
    setCartItems(newCartItems);
  };

  const clearCart = () => {
    // Early return if cart is disabled
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch");
      return;
    }

    setCartItems([]);
    setCurrentRestaurantId(null);
    setShippingInfo({
      name: "",
      tableNumber: "",
      orderNote: "",
    });
    SessionStorageManager.clearOrderNote();
  };

  // Temporary quantity functions
  const setTempQuantity = (
    itemId: string,
    quantity: number,
    variants: any[] = [],
    isAiSuggestion?: boolean
  ) => {
    if (quantity <= 0) {
      const newTempQuantities = { ...tempQuantities };
      delete newTempQuantities[itemId];
      setTempQuantities(newTempQuantities);

      // Also remove from session storage
      try {
        const savedItemCards = sessionStorage.getItem("restaurant-item-cards");
        if (savedItemCards) {
          const itemCardsData = JSON.parse(savedItemCards);
          delete itemCardsData[itemId];

          if (Object.keys(itemCardsData).length > 0) {
            sessionStorage.setItem(
              "restaurant-item-cards",
              JSON.stringify(itemCardsData)
            );
          } else {
            sessionStorage.removeItem("restaurant-item-cards");
          }

          // Trigger storage event for other components listening
          window.dispatchEvent(
            new StorageEvent("storage", {
              key: "restaurant-item-cards",
              newValue:
                Object.keys(itemCardsData).length > 0
                  ? JSON.stringify(itemCardsData)
                  : null,
              storageArea: sessionStorage,
            })
          );
        }
      } catch (error) {
        console.error("Error updating session storage from context:", error);
      }
    } else {
      setTempQuantities((prev) => ({
        ...prev,
        [itemId]: { quantity, variants, isAiSuggestion },
      }));
    }
  };

  const getTempQuantity = (itemId: string): number => {
    return tempQuantities[itemId]?.quantity || 0;
  };

  const commitTempQuantities = () => {
    // Find all items with temp quantities and add them to cart
    Object.entries(tempQuantities).forEach(([itemId, tempData]) => {
      if (tempData.quantity > 0) {
        // Find the item from the menu (you might need to pass this differently)
        // For now, we'll just update existing cart items or skip non-existent ones
        const existingCartItem = cartItems.find((item) => item.id === itemId);
        if (existingCartItem) {
          updateCartItemQuantity(itemId, tempData.quantity);
        }
      }
    });

    // Clear temp quantities after committing
    setTempQuantities({});
  };

  const clearTempQuantities = () => {
    setTempQuantities({});

    // Also clear session storage
    try {
      sessionStorage.removeItem("restaurant-item-cards");
      // Trigger storage event for other components listening
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: "restaurant-item-cards",
          newValue: null,
          storageArea: sessionStorage,
        })
      );
    } catch (error) {
      console.error("Error clearing session storage from context:", error);
    }
  };

  // UI actions
  const openCart = () => {
    if (isCartDisabled) {
      console.warn("Cart functionality is disabled for this branch");
      return;
    }
    setIsCartOpen(true);
  };
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(!isCartOpen);

  // Checkout actions
  const setTipPercentage = (percentage: number) => {
    setSelectedTipPercentage(percentage);
  };

  const updateShippingInfo = (info: Partial<ShippingInfo>) => {
    setShippingInfo((prev) => ({ ...prev, ...info }));
    if (Object.prototype.hasOwnProperty.call(info, "orderNote")) {
      SessionStorageManager.saveOrderNote(info.orderNote ?? "");
    }
  };

  const checkout = async () => {
    try {
      // Here you would typically send the order to your backend
      const order = {
        restaurantId: currentRestaurantId,
        items: cartItems,
        summary: cartSummary,
        shipping: shippingInfo,
        createdAt: new Date().toISOString(),
      };

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Clear cart after successful checkout
      clearCart();
      closeCart();

      // In a real app, you might redirect to a confirmation page
      return Promise.resolve();
    } catch (error) {
      console.error("Checkout failed:", error);
      return Promise.reject(error);
    }
  };

  // Utility functions
  const getItemQuantity = (itemId: string): number => {
    return cartItems.reduce((count, cartItem) => {
      if (cartItem.id !== itemId) {
        return count;
      }
      return count + cartItem.quantity;
    }, 0);
  };

  const getVariantSubsetQuantity = (
    itemId: string,
    variants: any[] = []
  ): number => {
    const targetData = getVariantComparisonData(variants);
    if (!targetData.normalizedList.length) {
      return 0;
    }

    return cartItems.reduce((count, cartItem) => {
      if (cartItem.id !== itemId) {
        return count;
      }
      const { normalizedList } = getVariantComparisonData(cartItem.variants);
      if (!normalizedList.length) {
        return count;
      }
      if (isVariantSubset(targetData.normalizedList, normalizedList)) {
        return count + cartItem.quantity;
      }
      return count;
    }, 0);
  };

  const getVariantQuantity = (itemId: string, variants: any[] = []): number => {
    const targetKey = getVariantComparisonData(variants).key;
    return cartItems.reduce((count, cartItem) => {
      if (cartItem.id !== itemId) {
        return count;
      }
      const { key } = getVariantComparisonData(cartItem.variants);
      if (key === targetKey) {
        return count + cartItem.quantity;
      }
      return count;
    }, 0);
  };

  const isItemInCart = (itemId: string): boolean => {
    return cartItems.some((cartItem) => cartItem.id === itemId);
  };

  const contextValue: ShoppingCartContextType = {
    // Cart state
    cartItems,
    isCartOpen,
    currentRestaurantId,
    isCartDisabled,

    // Cart calculations
    cartSummary,
    selectedTipPercentage,

    // Customer info
    shippingInfo,

    // Temporary quantities
    tempQuantities,

    // Actions
    addToCart,
    addToCartWithVariants,
    removeFromCart,
    removeFromCartWithVariants,
    updateCartItemQuantity,
    updateSpecialInstructions,
    clearCart,

    // Temporary quantity actions
    setTempQuantity,
    getTempQuantity,
    commitTempQuantities,
    clearTempQuantities,

    // UI actions
    openCart,
    closeCart,
    toggleCart,

    // Checkout actions
    setTipPercentage,
    updateShippingInfo,
    checkout,

    // Utility
    getItemQuantity,
    getVariantSubsetQuantity,
    getVariantQuantity,
    isItemInCart,
  };

  return (
    <ShoppingCartContext.Provider value={contextValue}>
      {children}
    </ShoppingCartContext.Provider>
  );
};

// Custom hook to use shopping cart context
export const useShoppingCart = (): ShoppingCartContextType => {
  const context = useContext(ShoppingCartContext);
  if (context === undefined) {
    throw new Error(
      "useShoppingCart must be used within a ShoppingCartProvider"
    );
  }
  return context;
};
